/**
 * CommsThread (conversation view)
 *
 * The unified per-person timeline, rendered as a messaging-style chat thread:
 * oldest at the top, newest at the bottom, day separators between calendar
 * days. SMS renders as bubbles (outbound primary/right, inbound gray/left) with a
 * sender + delivery-status meta line; calls render as compact event cards with
 * expandable speaker-labeled transcripts (Recruiter/Patient); notes as amber
 * sticky cards; system events as centered one-liners; email (Part 5) as a
 * bordered card with subject + delivery status.
 *
 * The data layer is unchanged from Chunk 2/3: GET /contacts/:id/communications
 * (newest-first, single-indexed on (contact_id, occurred_at DESC)), paginated
 * via the `before` cursor ("Load older" now sits at the TOP and scroll position
 * is preserved across prepends), live via the Supabase Realtime broadcast ping
 * on crm:contact:<id> plus a 60s fallback poll, and a `reloadToken` bump from
 * the composer. The component renders its own scroll region (flex-1 of the
 * parent conversation card), sticks to the bottom while the reader is near it,
 * and floats a "New messages" pill when they've scrolled up.
 *
 * `channelFilter='note'` narrows the whole thread to internal notes — the
 * recruiters' running record of a lead, consolidated (owner ask 2026-08-07,
 * entered by double-clicking the composer's Note pill). The narrowing is
 * server-side (?channel=note), so paging, the live merge and "Load older" all
 * stay inside notes instead of walking mixed windows that may hold none.
 *
 * `mask` (no-op default) is wired for Phase 7 role-aware masking: when true,
 * bodies on PHI-bearing channels (and call transcripts) are redacted while
 * structure/timestamps remain.
 *
 * Notes you can correct, @mentions and replies (docs/plans/crm-report-builder-search-collab-plan.md
 * B1.4 and B3.5; WP X-3). A note a person typed carries the "..." menu (notes/NoteActions), which
 * offers what notes/noteLogic noteActions allows the viewer: Edit in place (notes/NoteEditForm, its
 * text box the mention input), History (notes/NoteHistoryModal) and Delete with a reason
 * (notes/NoteReasonDialog NoteDeleteDialog). It says "edited" once changed and becomes the grey
 * marker (notes/DeletedNoteCard) once deleted. A recruitment-list mirror links to the recruitment list
 * instead; the AI screening summary and the notes the system wrote render as they always have. The
 * names a note mentions are highlighted (mentions/MentionText). Every live note a person wrote has
 * Reply: an inline mentions/MentionInput that posts the reply with reply_to_id (mentions/api
 * createNote). A reply sits indented under the note it answers when that note is loaded, else in its
 * own place with "Reply to an earlier note", which pages back until that note arrives (threadLayout).
 * Each change patches its row in place from the answer (notes/noteLogic patchCommunication); the
 * lead's ping and the 60 s poll bring the rest.
 */
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  StickyNote,
  Activity,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
  Mail,
  PhoneIncoming,
  PhoneOutgoing,
  FileText,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Paperclip,
  ImageOff,
  Voicemail,
  Play,
  X,
  CornerDownRight,
  Reply,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeContactChanges } from '../../lib/realtimeTransport';
import { Communication, TeamMember } from '../types';
import { dayKey, dayLabel, messageTimestamp, prettySlug, formatPhone, useIsCRMAdmin } from '../lib';
import DeletedNoteCard from '../notes/DeletedNoteCard';
import NoteActions from '../notes/NoteActions';
import NoteEditForm from '../notes/NoteEditForm';
import type { NoteEditOutcome } from '../notes/NoteEditForm';
import NoteHistoryModal from '../notes/NoteHistoryModal';
import { NoteDeleteDialog } from '../notes/NoteReasonDialog';
import { NOTE_BODY_MAX, editedWords, isDeletedNote, noteActions, noteKind, patchCommunication } from '../notes/noteLogic';
import { createNote, mentionErrorMessage } from '../mentions/api';
import MentionInput, { namedAtPost } from '../mentions/MentionInput';
import MentionText from '../mentions/MentionText';
import { noteMentionsOf, notifyFailedWords, pickedFromNote } from '../mentions/mentionLogic';
import type { MentionableUser } from '../mentions/types';
import { eligibilityBadge } from './shared';
import TranscriptModal from './TranscriptModal';
import FormResponseModal from './FormResponseModal';
import { Alert, btnGhost, btnPrimary, fieldCls } from './ui';

const PAGE = 50;
const PHI_CHANNELS = new Set(['sms', 'call', 'email']);
const MISSED_CALL_STATUSES = new Set(['no-answer', 'busy', 'failed', 'canceled']);

interface Props {
  contactId: string;
  reloadToken?: number;
  mask?: boolean;
  /** id -> TeamMember, for sender attribution on outbound items (useTeam().byId). */
  usersById?: Map<string, TeamMember>;
  /** This contact's display name + phone — labels the PATIENT side of call
   *  transcripts (falls back to the number, then a generic 'Patient'). */
  contactName?: string;
  contactPhone?: string | null;
  /** Called on every Realtime broadcast ping — lets the parent refresh rail
   * cards (RecordsCard etc.) without bumping reloadToken, which would force
   * this thread's scroll-to-bottom. */
  onPing?: () => void;
  /** Escalation banner deep-link: scroll to + flash this message ("take you to
   *  where the concern was"). `focusNonce` re-triggers the jump on repeat clicks. */
  focusCommId?: string | null;
  focusNonce?: number;
  /** Notes-only view: when 'note', the thread shows (and pages through) ONLY
   *  internal notes — the recruiters' running record of this lead, consolidated
   *  in one place. Entered by double-clicking the composer's Note pill. The
   *  narrowing happens server-side (?channel=), so "Load older" walks notes
   *  instead of paging mixed windows that may hold none. */
  channelFilter?: 'note' | null;
  /** Leaves the notes-only view. Absent = the banner shows no way out (the
   *  host owns the toggle), so hosts that pass a filter should pass this too. */
  onExitFilter?: () => void;
  /** A flagged question in the AI screening summary card was clicked — open the
   *  agent card's screen detail on it (owner ask 2026-08-05: the flags the
   *  agent posts here have to reach the answer they came from). Absent = the
   *  summary card stays read-only. */
  onFocusScreen?: (questionKey: string) => void;
}

/** Union two lists by id and sort newest-first (keeps loaded history on poll). */
function mergeDesc(a: Communication[], b: Communication[]): Communication[] {
  const map = new Map<string, Communication>();
  for (const x of a) map.set(x.id, x);
  for (const x of b) map.set(x.id, x);
  return [...map.values()].sort(
    (x, y) => new Date(y.occurred_at).getTime() - new Date(x.occurred_at).getTime()
  );
}

function bodyFor(c: Communication, mask: boolean): string | null {
  if (!c.body) return null;
  if (mask && PHI_CHANNELS.has(c.channel)) return '•••••';
  return c.body;
}

/** mm:ss for a call duration in seconds; null when absent/zero. */
function fmtDuration(sec?: number): string | null {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** SMS delivery status -> short human word ('Failed' renders red). */
function smsStatus(status: string | null): { label: string; failed: boolean } {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return { label: 'Delivered', failed: false };
  if (s === 'failed' || s === 'undelivered') return { label: 'Failed', failed: true };
  if (['queued', 'sent', 'accepted', 'sending'].includes(s)) return { label: 'Sent', failed: false };
  if (!s) return { label: '', failed: false };
  return { label: s.charAt(0).toUpperCase() + s.slice(1), failed: false };
}

// ---------------------------------------------------------------------------
// Notes (owner asks 2026-08-05): long notes collapse instead of dominating the
// thread, and the AI screening summary renders as real question→answer rows —
// glanceable progress up top, what's flagged and what's still missing visible
// without reading a text blob.
// ---------------------------------------------------------------------------

interface SummaryItem {
  q: string;
  /** The question_key, when the note was written by a server that stamps it.
   *  Older notes carry only the question text — the jump falls back to that,
   *  which AgentPanel resolves by label. */
  k?: string;
  a: string | null;
  declined?: boolean;
  review?: boolean;
  source?: string;
  quote?: string;
}
interface SummaryData {
  answered: number;
  total: number;
  review: number;
  in_progress?: boolean;
  items: SummaryItem[];
  missing: string[];
}

const SUMMARY_SOURCE_LABEL: Record<string, string> = {
  questionnaire: 'from questionnaire',
  landing: 'from web form',
  prescreen: 'from earlier screen',
  earlier_chat: 'from earlier chat',
  derived: 'calculated',
};

/**
 * Fallback for summary notes written before metadata.summary existed: rebuild
 * the structure from the body text (a format we write ourselves, so the line
 * shapes are stable). Best-effort — returns null when nothing parses, and the
 * caller falls back to the plain note body.
 */
function parseSummaryBody(body: string | null): SummaryData | null {
  if (!body) return null;
  const lines = body.split('\n');
  const counts = lines.find((l) => l.startsWith('Collected '));
  const m = counts?.match(/^Collected (\d+)\/(\d+) screening answers · (\d+) clear · (\d+) for review/);
  if (!m) return null;
  const items: SummaryItem[] = [];
  for (const line of lines) {
    const im = line.match(/^• (.+?): (.*)$/);
    if (!im) continue;
    let a = im[2];
    const review = a.includes(' ⚠ review');
    let quote: string | undefined;
    const qm = a.match(/ — "(.*)"\s*$/);
    if (qm) { quote = qm[1]; a = a.slice(0, qm.index); }
    a = a.replace(' ⚠ review', '');
    let source: string | undefined;
    const sm = a.match(/ \((questionnaire|web form|earlier screen|earlier text chat|calculated)\)\s*$/);
    if (sm) {
      source = { 'web form': 'landing', 'earlier screen': 'prescreen', 'earlier text chat': 'earlier_chat', calculated: 'derived' }[sm[1]] || sm[1];
      a = a.slice(0, sm.index);
    }
    const declined = a.trim() === 'declined to answer';
    items.push({ q: im[1], a: declined ? null : a.trim(), ...(declined ? { declined: true } : {}), ...(review ? { review: true } : {}), ...(source ? { source } : {}), ...(quote ? { quote } : {}) });
  }
  const missingLine = lines.find((l) => l.startsWith('Not collected: '));
  const missing = missingLine
    ? missingLine.slice('Not collected: '.length).replace(/ \(\+\d+ more\)$/, '').split('; ').filter(Boolean)
    : [];
  return {
    answered: Number(m[1]),
    total: Number(m[2]),
    review: Number(m[4]),
    in_progress: /\(in progress\)/.test(lines[0] || ''),
    items,
    missing,
  };
}

/** Small count pill for the summary card's glance row. */
function SummaryPill({ tone, children }: { tone: 'gray' | 'amber' | 'green'; children: ReactNode }) {
  const cls = tone === 'amber'
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    : tone === 'green'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}>
      {children}
    </span>
  );
}

/**
 * The AI screening summary as a structured, collapsible card. Collapsed by
 * default (it refreshes to the newest spot in the thread on every captured
 * answer — expanded, it shoved the conversation around every few minutes):
 * the glance row keeps progress + what's flagged readable at all times.
 *
 * Every flagged question is a link (owner ask 2026-08-05): clicking one opens
 * the agent card's screen detail on that answer, the same jump the escalation
 * banner makes — the flags the agent drops into the thread are the ones a
 * recruiter has to act on, so reading them here must reach them there.
 */
function AgentSummaryNote({ c, studyLine, onFocusScreen }: {
  c: Communication;
  studyLine: string | null;
  onFocusScreen?: (questionKey: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const meta: any = c.metadata || {};
  const data: SummaryData | null = meta.summary && Array.isArray(meta.summary.items)
    ? meta.summary
    : parseSummaryBody(c.body);
  const missingCount = data ? data.missing.length : 0;
  const flaggedItems = data ? data.items.filter((i) => i.review) : [];
  return (
    <div className="rounded-lg border-l-2 border-amber-400 bg-amber-50 p-3 dark:bg-amber-900/15">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <StickyNote className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            AI screening summary{studyLine ? ` · ${studyLine}` : ''}
            {data?.in_progress ? ' · in progress' : ''}
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-gray-400">
          {messageTimestamp(c.occurred_at)}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {data ? (
        <>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <SummaryPill tone="gray">{data.answered}/{data.total} answered</SummaryPill>
            {data.review > 0 && <SummaryPill tone="amber">⚠ {data.review} to review</SummaryPill>}
            {missingCount > 0
              ? <SummaryPill tone="gray">{missingCount} not collected</SummaryPill>
              : data.total > 0 && data.answered >= data.total
                ? <SummaryPill tone="green">all collected</SummaryPill>
                : null}
          </div>
          {!open && flaggedItems.length > 0 && (
            /* Collapsed, the flagged questions ARE the card — each one is its
               own link to the answer behind it. No wrap: the row stays one
               line tall so the card never grows in the thread. */
            <p className="mt-1.5 flex min-w-0 items-baseline gap-1 overflow-hidden text-xs text-amber-700 dark:text-amber-400">
              <span aria-hidden="true">⚠</span>
              <span className="min-w-0 truncate">
                {flaggedItems.map((it, i) => (
                  <span key={i}>
                    {i > 0 && <span className="text-amber-500/70"> · </span>}
                    {onFocusScreen ? (
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); onFocusScreen(it.k || it.q); }}
                        className="rounded underline decoration-amber-400 underline-offset-2 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-amber-200"
                        title="Open this answer in the agent's screening detail"
                      >
                        {it.q}
                      </button>
                    ) : it.q}
                  </span>
                ))}
              </span>
            </p>
          )}
          {open && (
            <div className="mt-2">
              <div className="divide-y divide-amber-200/70 dark:divide-amber-800/40">
                {data.items.map((it, i) => {
                  // A flagged row is the one a recruiter has to do something
                  // about, so it (and only it) becomes the link to the answer.
                  const linked = Boolean(it.review && onFocusScreen);
                  // Spans, not <p> — a linked row nests this inside a <button>,
                  // whose content model is phrasing only.
                  const body = (
                    <>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{it.q}</span>
                      <span className={`mt-0.5 block text-sm font-medium ${it.review ? 'text-amber-800 dark:text-amber-300' : 'text-gray-900 dark:text-gray-100'}`}>
                        {it.declined ? <span className="font-normal italic text-gray-500 dark:text-gray-400">Declined to answer</span> : it.a}
                        {it.review && <span className="ml-1.5 font-normal text-amber-700 dark:text-amber-400">⚠ review</span>}
                        {it.source && SUMMARY_SOURCE_LABEL[it.source] && (
                          <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">{SUMMARY_SOURCE_LABEL[it.source]}</span>
                        )}
                      </span>
                      {it.quote && (
                        <span className="mt-0.5 block text-xs italic text-gray-500 dark:text-gray-400">“{it.quote}”</span>
                      )}
                    </>
                  );
                  if (!linked) return <div key={i} className="py-1.5">{body}</div>;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onFocusScreen!(it.k || it.q)}
                      title="Open this answer in the agent's screening detail"
                      className="flex w-full items-center gap-2 rounded py-1.5 text-left transition-colors duration-150 hover:bg-amber-100/60 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-amber-900/25"
                    >
                      <span className="min-w-0 flex-1">{body}</span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
              {missingCount > 0 && (
                <div className="mt-2 border-t border-amber-200/70 pt-2 dark:border-amber-800/40">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Not collected ({missingCount})
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {data.missing.map((q, i) => (
                      <li key={i} className="text-xs text-gray-500 dark:text-gray-400">· {q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        // Nothing parseable (a very old or truncated note) — plain body,
        // clamped unless opened.
        c.body && (
          <p className={`mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200 ${open ? '' : 'line-clamp-3'}`}>
            {c.body}
          </p>
        )
      )}
    </div>
  );
}

/** How long a plain note can be before it collapses behind "Show more". */
const NOTE_CLAMP_CHARS = 300;
const NOTE_CLAMP_LINES = 5;

/** The note header's separator, " \xb7 " (an escape, so this file's own additions stay ASCII). */
const NOTE_SEP = ' \xb7 ';

/** A text-sized action under a note (Show more, Reply, the recruitment-list link): 44 px on a phone. */
const NOTE_ACTION_CLS =
  'inline-flex min-h-[44px] items-center gap-1 rounded text-xs font-medium text-amber-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-amber-400 sm:min-h-0';

interface NoteCardProps {
  c: Communication;
  author: string | null;
  body: string | null;
  /** "edited 2 min ago" (notes/noteLogic editedWords), after the author. */
  edited?: string | null;
  /** The "..." menu (notes/NoteActions), after the time. */
  menu?: ReactNode;
  /** The in-place editor: shown instead of the text and the actions under it. */
  editor?: ReactNode;
  /** The actions under the text: Reply, and a mirror's way to the recruitment list. */
  footer?: ReactNode;
  /** A line above the header: a reply's "Reply to an earlier note". */
  lead?: ReactNode;
}

/**
 * Plain amber note card; long bodies collapse (owner ask 2026-08-05). The names in the note's
 * metadata.mentions are highlighted (mentions/MentionText, an inline span, so the clamp and "Show
 * more" behave as before). Without the optional slots (a note the system wrote) it renders exactly
 * as it always has.
 */
function NoteCard({ c, author, body, edited = null, menu = null, editor = null, footer = null, lead = null }: NoteCardProps) {
  const [open, setOpen] = useState(false);
  const long = Boolean(body && (body.length > NOTE_CLAMP_CHARS || body.split('\n').length > NOTE_CLAMP_LINES));
  const mentions = noteMentionsOf(c.metadata);
  const stamp = <span className="text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>;
  const more = long && !editor ? (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className={footer ? NOTE_ACTION_CLS : 'mt-1 text-xs font-medium text-amber-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-amber-400'}
    >
      {open ? 'Show less' : 'Show more'}
    </button>
  ) : null;
  return (
    <div data-note-card={c.id} className="group rounded-lg border-l-2 border-amber-400 bg-amber-50 p-3 dark:bg-amber-900/15">
      {lead}
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <StickyNote className="h-3.5 w-3.5" /> Note{author ? ` · ${author}` : ''}{edited ? `${NOTE_SEP}${edited}` : ''}
        </span>
        {menu ? <span className="inline-flex shrink-0 items-center gap-1">{stamp}{menu}</span> : stamp}
      </div>
      {editor ? (
        <div className="mt-2">{editor}</div>
      ) : (
        body && (
          <p className={`mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200 ${long && !open ? 'line-clamp-4' : ''}`}>
            {mentions.length > 0 ? <MentionText body={body} mentions={mentions} /> : body}
          </p>
        )
      )}
      {footer && !editor ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-3">
          {more}
          {footer}
        </div>
      ) : (
        more
      )}
    </div>
  );
}

/**
 * A typed note edited in place: notes/NoteEditForm with the mention input as its text box. The
 * picked list starts from the teammates the note already names (pickedFromNote), so a kept "@Name"
 * stays a mention and a name taken out withdraws its unread mention. The edit sends the ids the text
 * still names, or no `mentions` key at all when the note named nobody and still names nobody (plan
 * B3.3; B13 N-7, M-6, N-3). The answer is the row as crm.edit_note wrote it, BEFORE the mention sync
 * rewrote metadata.mentions, so the names this edit sent are laid over it here (B13 N-3, M-3).
 */
function NoteEditor({ contactId, note, nameOf, onDone, onCancel }: {
  contactId: string;
  note: Communication;
  nameOf: (userId: string) => string | null;
  onDone: (note: Communication, outcome: NoteEditOutcome) => void;
  onCancel: () => void;
}) {
  const hadMentions = noteMentionsOf(note.metadata).length > 0;
  const [picked, setPicked] = useState<MentionableUser[]>(() => pickedFromNote(note.metadata));

  const mentionsFor = (text: string): string[] | undefined => {
    const { ids } = namedAtPost(text, picked);
    return ids.length > 0 || hadMentions ? ids : undefined;
  };

  const onSaved = (saved: Communication, outcome: NoteEditOutcome) => {
    if (outcome !== 'saved') {
      onDone(saved, outcome);
      return;
    }
    const { ids, users } = namedAtPost(saved.body ?? '', picked);
    if (ids.length === 0 && !hadMentions) {
      onDone(saved, outcome);
      return;
    }
    const meta = saved.metadata && typeof saved.metadata === 'object' && !Array.isArray(saved.metadata) ? saved.metadata : {};
    onDone({ ...saved, metadata: { ...meta, mentions: users.map((u) => ({ user_id: u.id, name: u.name })) } }, outcome);
  };

  return (
    <NoteEditForm
      contactId={contactId}
      note={note}
      nameOf={nameOf}
      onSaved={onSaved}
      onCancel={onCancel}
      mentionsFor={mentionsFor}
      renderInput={(input) => <MentionInput {...input} picked={picked} onPickedChange={setPicked} rows={4} />}
    />
  );
}

/**
 * Reply under a note (plan B3.5): an inline mention input whose text posts as a new note with
 * reply_to_id (mentions/api createNote; the server attaches a reply to a reply to the top-level note
 * and tells that note's author). Ctrl/Cmd+Enter posts. Esc closes the box: read in the bubble phase
 * and stopped here, so the call workspace around the thread stays open, and left alone when the
 * mention list already handled it. A refusal keeps the box and the text with the server's sentence.
 */
function ReplyBox({ contactId, targetId, onPosted, onCancel }: {
  contactId: string;
  targetId: string;
  /** Saved: the note as the server answered it, and the "we could not tell" line when notifying failed. */
  onPosted: (communication: Communication | null, warning: string | null) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<MentionableUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const boxId = useId();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const trimmed = text.trim();
  const canPost = !busy && trimmed !== '' && trimmed.length <= NOTE_BODY_MAX;

  const post = async () => {
    if (!canPost) return;
    const named = namedAtPost(text, picked);
    setBusy(true);
    setError(null);
    try {
      const res = await createNote(contactId, { body: trimmed, mentions: named.ids, reply_to_id: targetId });
      if (!alive.current) return;
      onPosted(res.communication, res.mentions.failed ? notifyFailedWords(named.users) : null);
    } catch (e) {
      if (!alive.current) return;
      setBusy(false);
      setError(mentionErrorMessage(e, 'The reply was not saved. Please try again.'));
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onCancel();
      return;
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      void post();
    }
  };

  return (
    <div
      data-reply-box={targetId}
      onKeyDown={onKeyDown}
      className="space-y-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800"
    >
      <label htmlFor={boxId} className="sr-only">
        Reply to this note
      </label>
      <MentionInput
        id={boxId}
        value={text}
        onChange={setText}
        picked={picked}
        onPickedChange={setPicked}
        disabled={busy}
        autoFocus
        rows={2}
        maxLength={NOTE_BODY_MAX}
        placeholder="Write a reply (type @ to mention a teammate)"
        className={`${fieldCls} block min-h-[64px] w-full resize-y`}
      />
      {error && (
        <Alert tone="error" size="sm">
          {error}
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="hidden text-[11px] text-gray-500 dark:text-gray-400 sm:inline">Ctrl+Enter to post, Esc to cancel</span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className={`${btnGhost} bg-white dark:bg-gray-800`}>
            Cancel
          </button>
          <button type="button" onClick={() => { void post(); }} disabled={!canPost} className={btnPrimary}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            Post reply
          </button>
        </div>
      </div>
    </div>
  );
}

/** How a "Reply to an earlier note" lookup went: paging back, not found, or the read failed. */
type ParentLookupState = 'loading' | 'missing' | 'failed';

/** A reply whose note is not loaded: "Reply to an earlier note", which pages back until it is. */
function EarlierNoteLink({ state, onLoad }: { state: ParentLookupState | null; onLoad: () => void }) {
  return (
    <p className="mb-1 flex flex-wrap items-center gap-x-1.5 text-xs text-amber-700 dark:text-amber-400">
      <button
        type="button"
        data-reply-parent=""
        onClick={onLoad}
        disabled={state === 'loading'}
        className="inline-flex min-h-[44px] items-center gap-1 rounded font-medium underline underline-offset-2 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 dark:hover:text-amber-200 sm:min-h-0"
      >
        <CornerDownRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Reply to an earlier note
        {state === 'loading' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
      </button>
      {state === 'missing' && (
        <span className="text-gray-500 dark:text-gray-400">The note it answers could not be found.</span>
      )}
      {state === 'failed' && (
        <span className="text-red-600 dark:text-red-400">The note it answers could not be loaded. Try again.</span>
      )}
    </p>
  );
}

/**
 * Where each row of the thread sits (plan B3.5; WP X-3). A reply (a note with reply_to_id) sits under
 * the note it answers when that note is loaded, oldest reply first; the server attaches a reply to a
 * reply to the top-level note, and a longer chain met here is followed up to its highest loaded note
 * all the same. A reply whose note is not loaded stands in its own place in the main column (an
 * orphan, which says "Reply to an earlier note"). A loop of replies (never written by the server)
 * shows each of its rows on its own.
 */
export interface ThreadLayout {
  /** The main column, oldest first: every row that is not a reply, and every orphan. */
  top: Communication[];
  /** A top row's replies, oldest first, by the top row's id. */
  replies: Map<string, Communication[]>;
  /** Every row's top row, by id (a top row is its own). */
  head: Map<string, string>;
  /** The replies whose note is not loaded. */
  orphans: Set<string>;
  /** The row rendered last: the bottom of the thread, which the scroll rules follow. */
  lastId: string | null;
}

/** The note a row answers, when its reply_to_id names another row. */
function replyTargetOf(c: Communication): string | null {
  const id = typeof c.reply_to_id === 'string' ? c.reply_to_id : '';
  return id && id !== c.id ? id : null;
}

/** Lays out the loaded rows (newest first, as the thread holds them); see ThreadLayout. */
export function threadLayout(items: Communication[]): ThreadLayout {
  const asc = [...items].reverse();
  const byId = new Map<string, Communication>();
  for (const c of asc) byId.set(c.id, c);

  const headOf = (c: Communication): string => {
    const seen = new Set<string>([c.id]);
    let cur = c;
    for (let step = 0; step <= asc.length; step += 1) {
      const target = replyTargetOf(cur);
      const parent = target ? byId.get(target) : undefined;
      if (!parent) return cur.id;
      if (seen.has(parent.id)) return c.id;
      seen.add(parent.id);
      cur = parent;
    }
    return c.id;
  };

  const top: Communication[] = [];
  const replies = new Map<string, Communication[]>();
  const head = new Map<string, string>();
  const orphans = new Set<string>();
  for (const c of asc) {
    const h = headOf(c);
    head.set(c.id, h);
    if (h === c.id) {
      top.push(c);
      const target = replyTargetOf(c);
      if (target && !byId.has(target)) orphans.add(c.id);
    } else {
      const list = replies.get(h);
      if (list) list.push(c);
      else replies.set(h, [c]);
    }
  }
  const last = top.length ? top[top.length - 1] : null;
  const lastReplies = last ? replies.get(last.id) : undefined;
  const lastId = lastReplies && lastReplies.length ? lastReplies[lastReplies.length - 1].id : last ? last.id : null;
  return { top, replies, head, orphans, lastId };
}

/** "Reply to an earlier note" pages back with the timeline route's largest page... */
const PARENT_SEARCH_LIMIT = 200;
/** ...at most this many times a press (2,000 older rows); a second press looks further back. */
const PARENT_SEARCH_PAGES = 10;

/** Email delivery status -> short human word (bounced/complained/failed red). */
function emailStatus(status: string | null): { label: string; failed: boolean } {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return { label: 'Delivered', failed: false };
  if (s === 'bounced') return { label: 'Bounced', failed: true };
  if (s === 'complained') return { label: 'Marked as spam', failed: true };
  if (s === 'failed') return { label: 'Failed', failed: true };
  if (['queued', 'sent'].includes(s)) return { label: 'Sent', failed: false };
  if (!s) return { label: '', failed: false };
  return { label: s.charAt(0).toUpperCase() + s.slice(1), failed: false };
}

/** Call status -> human word ('no-answer' -> 'No answer'). */
function callStatusWord(status: string | null): string {
  const s = (status || '').toLowerCase().replace(/[-_]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/**
 * Eligibility -> badge for the inline questionnaire card. Uses the shared
 * `eligibilityBadge` (single source of truth, identical to the lead-inbox study
 * chips and the historical "Screener answers" panel) but always returns a badge,
 * falling back to gray "Unknown" for an empty/unrecognized status.
 */
function leadEligibilityBadge(status: string | null): { label: string; cls: string } {
  return eligibilityBadge(status) || { label: 'Unknown', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
}

/** Highlight an individual answer that drove the eligibility outcome:
 *  'exclude' (disqualifying) → red, 'review' (flagged) → yellow, else none.
 *  Mirrors ScreenerPanel.answerHighlight so the inline card and the historical
 *  "Screener answers" panel highlight the SAME answers identically. */
function answerHighlight(kind: 'exclude' | 'review' | null | undefined): string {
  if (kind === 'exclude') return 'rounded-md bg-red-100 px-1.5 py-0.5 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (kind === 'review') return 'rounded-md bg-yellow-100 px-1.5 py-0.5 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'text-gray-900 dark:text-gray-100';
}

/**
 * One MMS attachment (an entry in communications.metadata.media). Fetches a
 * short-lived presigned URL via the audited /documents/:id/url endpoint, then
 * renders an inline image (tap to open full size) or — for non-images — an
 * open-able chip. Respects `mask` (Phase 7): masked media is never fetched.
 */
interface MediaRef {
  document_id: string;
  content_type?: string | null;
  filename?: string | null;
}
function MmsAttachment({ media, mask }: { media: MediaRef; mask: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isImage = String(media?.content_type || '').startsWith('image/');

  useEffect(() => {
    if (mask || !media?.document_id) return;
    let active = true;
    api
      .get(`/api/crm/documents/${media.document_id}/url`)
      .then((r) => { if (active) setUrl(r.data?.url || null); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [media?.document_id, mask]);

  const chip = (icon: ReactNode, label: string) => (
    <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10">
      {icon} {label}
    </span>
  );

  if (mask) return chip(<Paperclip className="h-3 w-3" />, 'Attachment hidden');
  if (failed) return chip(<ImageOff className="h-3 w-3" />, 'Attachment unavailable');

  if (isImage) {
    if (!url) return <div className="h-40 w-40 animate-pulse rounded-lg bg-black/10 dark:bg-white/10" />;
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={media.filename || 'MMS attachment'}
          className="max-h-60 max-w-full rounded-lg object-cover"
          loading="lazy"
        />
      </a>
    );
  }
  // Non-image (pdf/vcard/etc.): open-able chip once the URL resolves.
  return (
    <a
      href={url || undefined}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10 ${
        url ? '' : 'pointer-events-none opacity-60'
      }`}
    >
      <Paperclip className="h-3 w-3" /> {media.filename || 'Attachment'}
    </a>
  );
}

/**
 * Voicemail audio player. Click-to-load: the presigned URL is fetched ONLY when
 * the recruiter actually plays it, because issuing the URL writes a phi_accessed
 * audit row server-side — we must not audit-log a "play" for every voicemail just
 * because it scrolled into view. Respects `mask` (Phase 7).
 */
function VoicemailPlayer({ commId, mask }: { commId: string; mask: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (url || loading) return;
    setLoading(true);
    api
      .get(`/api/crm/voice/voicemail/${commId}/audio`)
      .then((r) => setUrl(r.data?.url || null))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  if (mask) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs dark:bg-white/10">
        <Voicemail className="h-3 w-3" /> Voicemail hidden
      </span>
    );
  }
  if (failed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-black/10 px-2 py-1 text-xs text-red-600 dark:bg-white/10 dark:text-red-400">
        <Voicemail className="h-3 w-3" /> Voicemail unavailable
      </span>
    );
  }
  if (!url) {
    return (
      <button
        onClick={load}
        disabled={loading}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-300 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-[36px]"
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        Play voicemail
      </button>
    );
  }
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio src={url} controls preload="none" className="mt-0.5 h-9 w-full max-w-[16rem]" />;
}

export default function CommsThread({ contactId, reloadToken = 0, mask = false, usersById, onPing, contactName, contactPhone, focusCommId = null, focusNonce = 0, onFocusScreen, channelFilter = null, onExitFilter }: Props) {
  const [items, setItems] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The call whose full transcript is open in the modal (stores the full
  // payload so TranscriptModal — rendered outside renderItem — has everything
  // it needs). null when no transcript modal is open.
  const [transcriptModal, setTranscriptModal] = useState<{
    id: string;
    transcript: any[];
    direction: string | null;
    recruiter: string | null;
  } | null>(null);
  // The form_responses id whose answers are open in the questionnaire modal
  // (opened from a "Questionnaire submitted" timeline line). null when closed.
  const [responseModal, setResponseModal] = useState<string | null>(null);
  const [showNewPill, setShowNewPill] = useState(false);
  // Escalation banner deep-link: the message currently flashed after a jump-to.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Notes you can correct and replies (plan B1.4, B3.5): one of each at a time. The note edited in
  // place, the note whose delete dialog is open, the note whose history is open, the note a reply is
  // being written to, and the "we could not tell" line a reply left behind.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Communication | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyNotice, setReplyNotice] = useState<{ targetId: string; text: string } | null>(null);
  // "Reply to an earlier note": the reply whose note is being paged in, and how that went.
  const [parentLookup, setParentLookup] = useState<{ replyId: string; state: ParentLookupState } | null>(null);
  // A jump this thread asks for itself: the note a reply answers, once it has been paged in.
  const [jump, setJump] = useState<{ id: string; nonce: number } | null>(null);
  // Who is reading: the menu offers what the server would allow them (display only; the SQL decides).
  const { user } = useAuth();
  const viewerId = user?.id ?? null;
  const isAdmin = useIsCRMAdmin();
  const nameOf = useCallback((id: string): string | null => usersById?.get(id)?.name ?? null, [usersById]);
  // Where each row sits: replies under the note they answer (threadLayout).
  const layout = useMemo(() => threadLayout(items), [items]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null); // the inner rows wrapper (observed for async height growth)
  // Does the reader WANT the newest message pinned? This is an intent latch, not
  // a position test: it drops to false the moment they scroll backwards (reading
  // history) and only comes back when they return to the bottom themselves, hit
  // the "New messages" pill, or send. Every programmatic scroll below is gated on
  // it, so nothing — an arrival, a lazy image resolving, a re-render — can yank a
  // reader out of the history they deliberately scrolled into.
  const stickRef = useRef(true);
  const lastScrollTopRef = useRef(0); // last observed scrollTop, to tell up-scroll from down
  const didInitialScrollRef = useRef(false); // jumped to bottom on first load?
  const prependHeightRef = useRef<number | null>(null); // scrollHeight before a "Load older" prepend
  const lastNewestIdRef = useRef<string | null>(null); // detect genuinely-new arrivals
  const forceScrollRef = useRef(false); // the recruiter just sent — follow their own message down
  const lookupGenRef = useRef(0); // bumped on a contact or filter switch: an earlier-note lookup in flight stands down

  // Fetch the newest page. reset:true replaces (contact switch); else merge (poll/refresh).
  const fetchNewest = useCallback(async (reset: boolean) => {
    if (reset) setLoading(true);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/communications`, {
        params: { limit: PAGE, ...(channelFilter ? { channel: channelFilter } : {}) },
      });
      const fetched: Communication[] = res.data?.communications || [];
      setItems((prev) => (reset ? fetched : mergeDesc(prev, fetched)));
      if (reset) setHasMore(!!res.data?.hasMore);
      setError(null);
    } catch (e: any) {
      if (reset) setError(e?.response?.data?.detail || e?.response?.data?.error || e.message);
    } finally {
      if (reset) setLoading(false);
    }
  }, [contactId, channelFilter]);

  // Reset + load when the contact changes (and reset scroll bookkeeping).
  useEffect(() => {
    didInitialScrollRef.current = false;
    lastNewestIdRef.current = null;
    prependHeightRef.current = null;
    stickRef.current = true;
    lastScrollTopRef.current = 0;
    forceScrollRef.current = false;
    setShowNewPill(false);
    // Whatever was open on the previous lead's notes closes with it.
    lookupGenRef.current += 1;
    setEditingId(null);
    setDeleting(null);
    setHistoryId(null);
    setReplyTo(null);
    setReplyNotice(null);
    setParentLookup(null);
    fetchNewest(true);
  }, [fetchNewest]);

  // Live: subscribe to the contact's "changed" ping through the realtime transport
  // adapter (WP-3.3). The backend (service_role) emits a content-free ping at each
  // comms write; we re-fetch through the authed, RLS-gated API (crm.* stays
  // server-only — no browser PHI grants). Under the default 'supabase' transport
  // this is byte-identical to the previous per-contact broadcast subscription;
  // under 'sse' the same ping arrives over the shared EventSource.
  // Ref keeps the subscription stable across parent re-renders (onPing is a
  // fresh closure each render; resubscribing per render would thrash the stream).
  const onPingRef = useRef<(() => void) | undefined>(onPing);
  onPingRef.current = onPing;
  useEffect(() => {
    const unsubscribe = subscribeContactChanges(contactId, () => {
      fetchNewest(false);
      onPingRef.current?.();
    });
    return unsubscribe;
  }, [contactId, fetchNewest]);

  // Fallback poll (60s) in case a broadcast ping is ever missed (merges, so any
  // loaded history is preserved).
  useEffect(() => {
    const t = setInterval(() => fetchNewest(false), 60000);
    return () => clearInterval(t);
  }, [fetchNewest]);

  // Escalation banner deep-link: scroll the flagged message into view and flash
  // it. Best-effort — a message not in the loaded window (rare for a fresh
  // flag) simply doesn't scroll. focusNonce re-fires on repeat clicks.
  useEffect(() => {
    if (!focusCommId) return undefined;
    const el = document.getElementById(`comm-${focusCommId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      stickRef.current = false; // we deliberately left the bottom
      setShowNewPill(false);
      setHighlightId(focusCommId);
    }
    const clear = window.setTimeout(
      () => setHighlightId((id) => (id === focusCommId ? null : id)),
      2800,
    );
    return () => window.clearTimeout(clear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommId, focusNonce]);

  // The same scroll and flash for a jump the thread asks for itself: the note a reply answers,
  // once "Reply to an earlier note" has paged it in (loadParent). Runs after the paged rows commit.
  useEffect(() => {
    if (!jump) return undefined;
    const el = document.getElementById(`comm-${jump.id}`);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      stickRef.current = false;
      setShowNewPill(false);
      setHighlightId(jump.id);
    }
    const clear = window.setTimeout(() => setHighlightId((id) => (id === jump.id ? null : id)), 2800);
    return () => window.clearTimeout(clear);
  }, [jump]);

  // Refresh when the parent signals a new message/note was sent — and follow
  // the recruiter's own message to the bottom even if they were scrolled up.
  // Not every reloadToken bump appends a message — an agent-draft decision, a
  // re-enroll, a rail action all bump it too. Disarm on a short timer so a bump
  // that added nothing can't leave the flag armed for minutes and then yank a
  // reader out of history the next time ANY message lands.
  useEffect(() => {
    if (!reloadToken) return undefined;
    forceScrollRef.current = true;
    fetchNewest(false);
    const t = window.setTimeout(() => { forceScrollRef.current = false; }, 4000);
    return () => window.clearTimeout(t);
  }, [reloadToken, fetchNewest]);

  const loadOlder = async () => {
    if (!items.length || loadingMore) return;
    stickRef.current = false; // asking for history is as deliberate as it gets
    setLoadingMore(true);
    try {
      const before = items[items.length - 1].occurred_at;
      const res = await api.get(`/api/crm/contacts/${contactId}/communications`, {
        params: { limit: PAGE, before, ...(channelFilter ? { channel: channelFilter } : {}) },
      });
      const older: Communication[] = res.data?.communications || [];
      // Capture the pre-prepend height HERE — after the await — so a live
      // broadcast/poll merge that committed while the request was in flight is
      // already part of the captured height and can't consume the adjustment
      // meant for this prepend.
      prependHeightRef.current = scrollRef.current?.scrollHeight ?? null;
      setItems((prev) => mergeDesc(prev, older));
      setHasMore(!!res.data?.hasMore);
    } catch {
      /* keep what we have */
      prependHeightRef.current = null;
    } finally {
      setLoadingMore(false);
    }
  };

  // "Reply to an earlier note": page back with the route's largest pages until the note the reply
  // answers is loaded, then jump to it; the reply moves under it by itself (threadLayout). Each page
  // is a prepend, so the reader's place is kept as "Load older" keeps it. Bounded at
  // PARENT_SEARCH_PAGES pages; a contact or filter switch meanwhile stands it down.
  const loadParent = async (reply: Communication) => {
    const parentId = replyTargetOf(reply);
    if (!parentId || parentLookup?.state === 'loading') return;
    const gen = lookupGenRef.current;
    setParentLookup({ replyId: reply.id, state: 'loading' });
    stickRef.current = false;
    let list = items;
    let more = hasMore;
    try {
      for (let page = 0; page < PARENT_SEARCH_PAGES && more && !list.some((x) => x.id === parentId); page += 1) {
        const before = list.length ? list[list.length - 1].occurred_at : null;
        if (!before) break;
        const res = await api.get(`/api/crm/contacts/${contactId}/communications`, {
          params: { limit: PARENT_SEARCH_LIMIT, before, ...(channelFilter ? { channel: channelFilter } : {}) },
        });
        if (gen !== lookupGenRef.current) return;
        const older: Communication[] = res.data?.communications || [];
        more = !!res.data?.hasMore && older.length > 0;
        list = mergeDesc(list, older);
        prependHeightRef.current = scrollRef.current?.scrollHeight ?? null;
        setItems((prev) => mergeDesc(prev, older));
        setHasMore(!!res.data?.hasMore);
      }
    } catch {
      if (gen === lookupGenRef.current) setParentLookup({ replyId: reply.id, state: 'failed' });
      return;
    }
    if (gen !== lookupGenRef.current) return;
    if (list.some((x) => x.id === parentId)) {
      setParentLookup(null);
      setJump({ id: parentId, nonce: Date.now() });
    } else {
      setParentLookup({ replyId: reply.id, state: 'missing' });
    }
  };

  // An edit ended (saved, the same text, or the teammate's text kept): patch the row in place from
  // the answer. mergeDesc prefers the fresh copy of an id, so the next poll agrees.
  const onEditDone = (note: Communication, outcome: NoteEditOutcome) => {
    setEditingId(null);
    setItems((prev) => patchCommunication(prev, note));
    // Keep theirs carries only the text, count, editor and time of their version: read the rest.
    if (outcome === 'kept_theirs') void fetchNewest(false);
  };

  // A delete went through: the row becomes the grey marker in place (body '', deleted_at, and the
  // reason stamped in its metadata); nothing can be edited or answered on it any more.
  const onDeleted = (note: Communication) => {
    setDeleting(null);
    setItems((prev) => patchCommunication(prev, note));
    setEditingId((id) => (id === note.id ? null : id));
    setReplyTo((id) => (id === note.id ? null : id));
  };

  const openReply = (c: Communication) => {
    setReplyNotice(null);
    setReplyTo(c.id);
  };

  // A reply was saved: it shows at once from the answer, under the note it answers. The answer
  // carries no created_at (addNote's select), so the newest page is read again for the row the
  // menu needs (plan B13, M-3) rather than waiting for the lead's ping.
  const onReplyPosted = (targetId: string, communication: Communication | null, warning: string | null) => {
    setReplyTo(null);
    setReplyNotice(warning ? { targetId, text: warning } : null);
    if (communication && communication.id) setItems((prev) => mergeDesc(prev, [communication]));
    void fetchNewest(false);
  };

  // Scroll behavior: first load jumps to the bottom; prepends preserve position;
  // new arrivals smooth-scroll only if the reader was already near the bottom,
  // otherwise the floating "New messages" pill appears. The bottom is the row
  // rendered LAST (threadLayout): a reply to an older note sits under that note,
  // so it is not an arrival at the bottom and moves nothing (plan B3.5).
  const newestId = layout.lastId;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prependHeightRef.current != null) {
      el.scrollTop += el.scrollHeight - prependHeightRef.current;
      lastScrollTopRef.current = el.scrollTop;
      prependHeightRef.current = null;
      lastNewestIdRef.current = newestId;
      return;
    }
    if (!didInitialScrollRef.current) {
      if (items.length) {
        el.scrollTop = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
        didInitialScrollRef.current = true;
        lastNewestIdRef.current = newestId;
      }
      return;
    }
    if (newestId && newestId !== lastNewestIdRef.current) {
      lastNewestIdRef.current = newestId;
      if (stickRef.current || forceScrollRef.current) {
        stickRef.current = true; // a recruiter's own send re-pins the thread
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        setShowNewPill(false);
      } else {
        setShowNewPill(true);
      }
      forceScrollRef.current = false;
    }
  }, [items, newestId]);

  // Keep the view pinned to the newest message when timeline content grows
  // AFTER the one-shot initial jump. MMS image attachments swap a 160px
  // placeholder for a real image, `<img loading="lazy">` decodes post-layout,
  // and web-font/line-wrap reflow all increase height asynchronously — without
  // this, the initial scrollHeight measurement (useLayoutEffect line ~371) is
  // stale and the thread opens parked ABOVE the newest message. The observer
  // re-pins only while the reader is still stuck to the bottom (stickRef — true
  // through the initial load, false the instant they scroll backwards), and never
  // mid-prepend, so it can't fight "Load older" or a reader reading history.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (loading || !el || !content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (prependHeightRef.current != null) return;
      if (stickRef.current) {
        el.scrollTop = el.scrollHeight;
        lastScrollTopRef.current = el.scrollTop;
      }
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [loading, contactId]);

  // Any backward scroll is read as "I'm reading history" and unsticks the thread
  // for good; only landing back at the bottom (or the pill / a send) re-sticks it.
  // Our own programmatic scrolls all move DOWN and end at the bottom, so they
  // re-stick naturally instead of tripping the unstick.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (el.scrollTop < lastScrollTopRef.current - 2) stickRef.current = false;
    if (atBottom) {
      stickRef.current = true;
      setShowNewPill(false);
    }
    lastScrollTopRef.current = el.scrollTop;
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    stickRef.current = true;
    setShowNewPill(false);
  };

  const senderName = (userId: string | null): string | null => {
    if (userId === null || userId === undefined) return 'Automated';
    return usersById?.get(userId)?.name || null;
  };

  // A note someone wrote (plan B1.4, B3.5). A note the system wrote renders exactly as before. A typed
  // note carries the menu (what noteActions allows the viewer), the in-place editor, the edited line
  // and Reply; a reply whose note is not loaded also says "Reply to an earlier note". A
  // recruitment-list mirror carries Reply and the way to the recruitment list, where it is edited.
  const renderNote = (c: Communication, author: string | null, body: string | null): ReactNode => {
    const kind = noteKind(c);
    if (kind === 'system') return <NoteCard key={c.id} c={c} author={author} body={body} />;
    const typed = kind === 'typed';
    const editing = typed && editingId === c.id;
    const actions = typed ? noteActions(c, { userId: viewerId, isAdmin }) : null;
    const offered = Boolean(actions && (actions.canEdit || actions.canDelete || actions.canViewHistory));
    const lookup = parentLookup && parentLookup.replyId === c.id ? parentLookup.state : null;
    return (
      <NoteCard
        key={c.id}
        c={c}
        author={author}
        body={body}
        edited={editedWords(c, { nameOf })}
        menu={actions && offered && !editing ? (
          <NoteActions
            note={c}
            actions={actions}
            onEdit={() => setEditingId(c.id)}
            onDelete={() => setDeleting(c)}
            onHistory={() => setHistoryId(c.id)}
            revealOnHover
            className="-my-3 -mr-2 sm:-my-2 sm:-mr-2.5"
          />
        ) : null}
        editor={editing ? (
          <NoteEditor contactId={contactId} note={c} nameOf={nameOf} onDone={onEditDone} onCancel={() => setEditingId(null)} />
        ) : null}
        lead={typed && layout.orphans.has(c.id) ? (
          <EarlierNoteLink state={lookup} onLoad={() => { void loadParent(c); }} />
        ) : null}
        footer={(
          <>
            <button
              type="button"
              data-note-reply={c.id}
              onClick={() => openReply(c)}
              aria-expanded={replyTo === c.id}
              className={NOTE_ACTION_CLS}
            >
              <Reply className="h-3.5 w-3.5" aria-hidden="true" />
              Reply
            </button>
            {kind === 'recruit_mirror' && (
              <Link to="/crm/recruit" data-recruit-link="" className={NOTE_ACTION_CLS}>
                Edit this in the recruitment list
                <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            )}
          </>
        )}
      />
    );
  };

  const renderItem = (c: Communication): ReactNode => {
    const body = bodyFor(c, mask);

    // ----- system: questionnaire submission -> rich answers card -----
    // A landing-page (or texted-form) submission posts its answers here so the
    // recruiter reads them inline in the conversation. Values mirror the
    // historical "Screener answers" panel; answer VALUES are PHI, so they
    // redact under `mask` while labels/structure stay.
    if (c.channel === 'system' && c.metadata?.event === 'lead_submitted') {
      const answers: { question: string; answer: string; eligibility_kind?: 'exclude' | 'review' | null }[] =
        Array.isArray(c.metadata?.answers) ? c.metadata.answers : [];
      const qName: string | null = c.metadata?.questionnaire_name || null;
      const study: string | null =
        c.metadata?.study_title || (c.metadata?.study_slug ? prettySlug(c.metadata.study_slug) : null);
      const badge = leadEligibilityBadge(c.metadata?.eligibility_status || null);
      // Partial-promoted leads post the SAME card as "in progress" and finalize it
      // on complete(); fall back to complete for legacy cards lacking the flag.
      const isComplete = c.metadata?.submission_complete !== false;
      return (
        <div
          key={c.id}
          className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 border-l-primary-400"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 dark:text-primary-300">
              <ClipboardList className="h-3.5 w-3.5" /> {isComplete ? 'Questionnaire submitted' : 'Questionnaire in progress'}
              {!isComplete && (
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  in progress
                </span>
              )}
            </span>
            <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
          </div>
          {(study || qName || badge) && (
            <div className="mt-1 flex items-start justify-between gap-2">
              <div className="min-w-0">
                {study && <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{study}</p>}
                {qName && qName !== study && <p className="truncate text-xs text-gray-400">{qName}</p>}
              </div>
              {badge && (
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
              )}
            </div>
          )}
          {answers.length > 0 ? (
            <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
              {answers.map((a, i) => (
                <div key={i} className="flex items-start justify-between gap-4 py-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{a.question}</span>
                  <span className={`min-w-0 break-words text-right text-sm font-medium ${answerHighlight(a.eligibility_kind)}`}>
                    {mask ? '•••' : a.answer}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-xs text-gray-400">{body || 'No answers recorded.'}</p>
          )}
        </div>
      );
    }

    // ----- system: texted/emailed questionnaire completed -> clickable line
    // that opens the answers in a modal. The completePublicResponse side-effect
    // stamps metadata.response_id; we route the actual answer fetch through the
    // PHI-audited /form-responses/:id endpoint (no answer VALUES ride in the
    // timeline row). Older events without a response_id fall through to the
    // plain one-liner below. -----
    if (c.channel === 'system' && c.metadata?.event === 'form_completed' && c.metadata?.response_id) {
      const rid = String(c.metadata.response_id);
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <button
            type="button"
            onClick={() => setResponseModal(rid)}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/40"
          >
            <ClipboardList className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{c.body || 'Questionnaire submitted'}</span>
            <span className="shrink-0 underline underline-offset-2">View answers</span>
            <span className="shrink-0 text-primary-400 dark:text-primary-500/70">· {messageTimestamp(c.occurred_at)}</span>
          </button>
        </div>
      );
    }

    // ----- system: centered one-liner -----
    // Most system events (status/owner/study changes, callback booking, etc.)
    // stamp who did it in metadata.by_user_id, but the body text never names
    // them — surface it here, once, for every such event instead of teaching
    // each writer to embed a name in its body. A PRESENT key (even null, e.g.
    // a patient-booked callback) means the event captured provenance and is
    // worth a label; an ABSENT key (older/automated writers that never
    // recorded an actor) shows nothing rather than a misleading "Automated".
    if (c.channel === 'system') {
      const hasActor = c.metadata && 'by_user_id' in c.metadata;
      const actor = hasActor ? senderName(c.metadata.by_user_id ?? null) : null;
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <span className="inline-flex items-center gap-1.5 text-center text-xs italic text-gray-400 dark:text-gray-500">
            <Activity className="h-3 w-3 shrink-0" />
            {c.body || 'System event'}{actor ? ` · ${actor}` : ''} · {messageTimestamp(c.occurred_at)}
          </span>
        </div>
      );
    }

    // ----- note: amber sticky card. The AI screening summary gets its own
    // structured, collapsible renderer (it refreshes to the newest spot in
    // the thread on every captured answer — see AgentSummaryNote); every
    // other note collapses when long (owner asks 2026-08-05). -----
    if (c.channel === 'note') {
      if (c.metadata?.event === 'agent_screening_summary') {
        const header = (c.body || '').split('\n')[0] || '';
        const parsedStudy = header.match(/^AI agent screening summary — (.+?)(?: \(in progress\))?$/)?.[1] || null;
        const studyLine = parsedStudy && parsedStudy !== 'study' ? parsedStudy : null;
        return <AgentSummaryNote key={c.id} c={c} studyLine={studyLine} onFocusScreen={onFocusScreen} />;
      }
      // A deleted note leaves the grey marker where it was (decision N3 a); its row's body is ''.
      if (isDeletedNote(c)) {
        return <DeletedNoteCard key={c.id} contactId={contactId} note={c} nameOf={nameOf} isAdmin={isAdmin} />;
      }
      const author = c.user_id ? usersById?.get(c.user_id)?.name : null;
      return renderNote(c, author || null, body);
    }

    const outbound = c.direction === 'outbound';

    // ----- sms/email skipped by the automation worker (opt-out/suppression at
    // send, merged contact, missing comprehensive form): body is null, the WHY
    // lives in metadata.reason — an empty bubble would hide it. Centered
    // one-liner.
    if ((c.channel === 'sms' || c.channel === 'email') && c.status === 'skipped' && !c.body) {
      const reason = String(c.metadata?.reason || 'not sent').replace(/_/g, ' ');
      return (
        <div key={c.id} className="flex items-center justify-center py-0.5">
          <span className="inline-flex items-center gap-1.5 text-center text-xs italic text-gray-400 dark:text-gray-500">
            <Activity className="h-3 w-3 shrink-0" />
            {c.channel === 'email' ? 'Email' : 'SMS'} skipped — {reason} · {messageTimestamp(c.occurred_at)}
          </span>
        </div>
      );
    }

    // ----- sms: chat bubbles -----
    if (c.channel === 'sms') {
      const st = smsStatus(c.status);
      const sender = outbound ? senderName(c.user_id) : null;
      const mediaList: MediaRef[] = Array.isArray(c.metadata?.media) ? c.metadata.media : [];
      const mediaErrors: any[] = Array.isArray(c.metadata?.media_errors) ? c.metadata.media_errors : [];
      const hasMedia = mediaList.length > 0 || mediaErrors.length > 0;
      return (
        <div key={c.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
          <div className={`flex min-w-0 max-w-[75%] flex-col ${outbound ? 'items-end' : 'items-start'}`}>
            {/* overflow-wrap:anywhere, not just break-words: a long unbroken
                token (a signed study-doc link) must shrink the bubble's
                min-content width, or the right-aligned bubble spills left. */}
            <div
              className={`max-w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-2xl px-3.5 py-2 text-sm ${
                outbound
                  ? 'rounded-br-md bg-primary-600 text-white'
                  : 'rounded-bl-md bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
              }`}
            >
              {mediaList.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {mediaList.map((m, i) => (
                    <MmsAttachment key={m.document_id || i} media={m} mask={mask} />
                  ))}
                </div>
              )}
              {mediaErrors.length > 0 && (
                <div className={`${mediaList.length ? 'mt-1.5 ' : ''}text-xs opacity-80`}>
                  ⚠ {mediaErrors.length} attachment{mediaErrors.length === 1 ? '' : 's'} couldn’t be saved
                </div>
              )}
              {body ? <div className={hasMedia ? 'mt-1.5' : ''}>{body}</div> : hasMedia ? null : ''}
            </div>
            <div className={`mt-0.5 px-1 text-[11px] text-gray-400 ${outbound ? 'text-right' : 'text-left'}`}>
              {messageTimestamp(c.occurred_at)}
              {/* Texting-agent sends carry metadata.agent (§5.2) — a subtle
                  "Team · Agent" attribution instead of a human sender name. */}
              {outbound && (c.metadata as any)?.agent ? (
                <span> · <span className="font-medium text-primary-600 dark:text-primary-400">Team · Agent</span></span>
              ) : sender ? <span> · {sender}</span> : null}
              {outbound && st.label ? (
                <span>
                  {' · '}
                  {st.failed ? <span className="font-medium text-red-500 dark:text-red-400">Failed</span> : st.label}
                </span>
              ) : null}
            </div>
            {c.metadata?.error_code && (
              <p className={`px-1 text-xs text-red-600 dark:text-red-400 ${outbound ? 'text-right' : ''}`}>
                ⚠ delivery error {c.metadata.error_code}{c.metadata.error_message ? `: ${c.metadata.error_message}` : ''}
              </p>
            )}
            {c.metadata?.overridden && (
              <p className={`px-1 text-xs text-amber-600 dark:text-amber-400 ${outbound ? 'text-right' : ''}`}>
                ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
              </p>
            )}
          </div>
        </div>
      );
    }

    // ----- email (Part 5): bordered card with subject + delivery status -----
    if (c.channel === 'email') {
      const st = emailStatus(c.status);
      const sender = outbound ? senderName(c.user_id) : null;
      const subject = c.metadata?.subject ? String(c.metadata.subject) : null;
      // Study files sent with the email (the composer's "Attach study files" —
      // e.g. the ICF). sendManualEmail stamps compact refs on
      // metadata.attachments only after the send went out, so a listed file
      // really was attached. Names only; the bytes are never on the row.
      const attached: { filename?: string }[] = Array.isArray(c.metadata?.attachments) ? c.metadata.attachments : [];
      return (
        <div
          key={c.id}
          className={`rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 ${
            outbound ? 'border-l-primary-400' : 'border-l-gray-300 dark:border-l-gray-600'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              <Mail className={`h-3.5 w-3.5 ${outbound ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
              Email
              {sender ? <span> · {sender}</span> : null}
              {st.label ? (
                <span>
                  {' · '}
                  {st.failed ? <span className="font-medium text-red-500 dark:text-red-400">{st.label}</span> : st.label}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
          </div>
          {subject && (
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{mask ? '•••••' : subject}</p>
          )}
          {body && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">{body}</p>}
          {attached.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {attached.map((a, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                >
                  <Paperclip className="h-3 w-3" /> {mask ? 'Attachment hidden' : a.filename || 'Attachment'}
                </span>
              ))}
            </div>
          )}
          {c.metadata?.error_message && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">⚠ {c.metadata.error_message}</p>
          )}
          {c.metadata?.overridden && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
            </p>
          )}
        </div>
      );
    }

    // ----- call: full-width compact event card -----
    if (c.channel === 'call') {
      const s = (c.status || '').toLowerCase();
      const isVoicemail = !!c.metadata?.voicemail_s3_key || c.metadata?.voicemail === true;
      const missed = MISSED_CALL_STATUSES.has(s) && !isVoicemail;
      const Icon = isVoicemail ? Voicemail : (c.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing);
      const duration = fmtDuration(c.metadata?.duration_sec || c.metadata?.voicemail_duration_sec);
      const statusWord = isVoicemail ? '' : callStatusWord(c.status);
      const recruiter = c.user_id ? usersById?.get(c.user_id)?.name : null;
      const transcript: any[] = Array.isArray(c.metadata?.transcript) ? c.metadata.transcript : [];
      const hasTranscript = transcript.length > 0;
      return (
        <div
          key={c.id}
          className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
        >
          <span
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
              missed
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {isVoicemail ? 'Voicemail' : c.direction === 'inbound' ? 'Incoming call' : 'Outgoing call'}
                {duration && <span className="font-normal text-gray-500 dark:text-gray-400"> · {duration}</span>}
                {statusWord && (
                  <span className={`font-normal ${missed ? 'text-red-500 dark:text-red-400' : 'text-gray-400'}`}>
                    {' · '}{statusWord}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
            </div>
            {recruiter && <p className="text-xs text-gray-400">{recruiter}</p>}
            {isVoicemail && <VoicemailPlayer commId={c.id} mask={mask} />}
            {body && !hasTranscript && (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">{body}</p>
            )}
            {c.metadata?.overridden && (
              <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                ↪ test override → {c.metadata.sent_to} (intended {c.metadata.intended_to || '—'})
              </p>
            )}
            {hasTranscript && (
              <div className="mt-1">
                {/* Brief collapsed preview: first ~2 segments, same speaker
                    logic as the modal, truncated so the card stays short. */}
                <div className="space-y-0.5 text-xs leading-snug text-gray-400 dark:text-gray-500">
                  {transcript.slice(0, 2).map((seg, i) => {
                    const rec = c.direction === 'inbound'
                      ? seg.track === 'outbound_track'
                      : seg.track === 'inbound_track';
                    const speaker = rec
                      ? (recruiter || 'Recruiter')
                      : (contactName || (contactPhone ? formatPhone(contactPhone) : null) || 'Patient');
                    const text = mask ? '•••••' : String(seg.text || '');
                    return (
                      <p key={i} className="line-clamp-1">
                        <span className="font-medium text-gray-500 dark:text-gray-400">{speaker}:</span>{' '}
                        {mask ? '•••••' : (text.length > 100 ? `${text.slice(0, 100)}…` : text)}
                      </p>
                    );
                  })}
                </div>
                <button
                  onClick={() => setTranscriptModal({
                    id: c.id,
                    transcript,
                    direction: c.direction ?? null,
                    recruiter: recruiter ?? null,
                  })}
                  className="mt-0.5 inline-flex min-h-[44px] items-center gap-1 rounded text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-[28px]"
                >
                  <FileText className="h-3.5 w-3.5" />
                  View full transcript ({transcript.length})
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ----- email (future) / anything else: bordered generic card -----
    const DirIcon = outbound ? ArrowUpRight : ArrowDownLeft;
    return (
      <div
        key={c.id}
        className={`rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 border-l-2 ${
          outbound ? 'border-l-primary-400' : 'border-l-gray-300 dark:border-l-gray-600'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <DirIcon className={`h-3.5 w-3.5 ${outbound ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
            {c.channel}{c.direction ? ` · ${c.direction}` : ''}
            {c.status ? <span className="text-gray-400"> · {c.status}</span> : null}
          </span>
          <span className="text-[11px] text-gray-400">{messageTimestamp(c.occurred_at)}</span>
        </div>
        {body && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">{body}</p>}
      </div>
    );
  };

  // Notes-only banner. It carries the ONLY way back out (double-click is a poor
  // exit, and useless on touch), so it renders in EVERY branch below — loading,
  // error, empty and the thread itself.
  const filterBar =
    channelFilter === 'note' ? (
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 dark:border-amber-900/50 dark:bg-amber-900/20">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
          <StickyNote className="h-3.5 w-3.5 shrink-0" />
          Notes only
          {!loading && !error && (
            <span className="font-normal text-amber-700/80 dark:text-amber-300/70">
              · {items.length}{hasMore ? '+' : ''} note{items.length === 1 && !hasMore ? '' : 's'}
            </span>
          )}
        </span>
        {onExitFilter && (
          <button
            type="button"
            onClick={onExitFilter}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
          >
            <X className="h-3.5 w-3.5" /> Show everything
          </button>
        )}
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <Alert tone="error" icon={false}>{error}</Alert>
        </div>
      </div>
    );
  }

  // The C-12 "a draft is waiting — go review it in the console" strip used to
  // sit here. It is gone: AgentDraftDock now renders the actual draft, with
  // Approve / Edit / Reject, immediately below this thread. A strip pointing
  // at the console would be both redundant and wrong.

  if (!items.length) {
    const notesOnly = channelFilter === 'note';
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {filterBar}
        <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          {notesOnly ? (
            <StickyNote className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          ) : (
            <MessageSquare className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          )}
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {notesOnly ? 'No notes on this lead yet' : 'No messages yet'}
          </p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {notesOnly ? 'Add an internal note below and it lands here.' : 'Send a text or add a note below.'}
          </p>
        </div>
      </div>
    );
  }

  // Render ascending (oldest at top) with a separator at each calendar-day change.
  // A reply sits indented under the note it answers, in that note's place and day
  // (threadLayout); the reply being written and a "we could not tell" line close
  // the thread they belong to. Every row, a reply included, keeps its comm-<id>
  // anchor for the jumps.
  const rows: ReactNode[] = [];
  let lastDay: string | null = null;
  const row = (c: Communication) => (
    <div
      key={c.id}
      id={`comm-${c.id}`}
      className={`scroll-mt-2 rounded-xl transition-colors duration-500 ${
        highlightId === c.id
          ? 'bg-primary-50 ring-1 ring-primary-300 dark:bg-primary-900/20 dark:ring-primary-700'
          : ''
      }`}
    >
      {renderItem(c)}
    </div>
  );
  const replyGroup = replyTo ? layout.head.get(replyTo) ?? null : null;
  const noticeGroup = replyNotice ? layout.head.get(replyNotice.targetId) ?? null : null;
  for (const c of layout.top) {
    const k = dayKey(c.occurred_at);
    if (k !== lastDay) {
      lastDay = k;
      rows.push(
        <div key={`day-${k}`} className="flex items-center gap-3 py-1.5">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400">{dayLabel(c.occurred_at)}</span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      );
    }
    rows.push(row(c));
    const replies = layout.replies.get(c.id) || [];
    const target = replyTo && replyGroup === c.id ? replyTo : null;
    const notice = replyNotice && noticeGroup === c.id ? replyNotice.text : null;
    if (replies.length || target || notice) {
      rows.push(
        <div
          key={`replies-${c.id}`}
          data-replies-of={c.id}
          className="ml-4 space-y-2 border-l-2 border-amber-200 pl-3 dark:border-amber-900/60 sm:ml-6"
        >
          {replies.map((r) => row(r))}
          {target && (
            <ReplyBox
              key={`reply-box-${target}`}
              contactId={contactId}
              targetId={target}
              onPosted={(communication, warning) => onReplyPosted(target, communication, warning)}
              onCancel={() => setReplyTo(null)}
            />
          )}
          {notice && (
            <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
              {notice}
            </p>
          )}
        </div>,
      );
    }
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {filterBar}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-3">
        {hasMore && (
          <div className="flex justify-center pb-2">
            <button
              onClick={loadOlder}
              disabled={loadingMore}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          </div>
        )}
        <div ref={contentRef} className="space-y-2">{rows}</div>
      </div>
      {showNewPill && (
        <button
          onClick={jumpToBottom}
          className="absolute bottom-3 left-1/2 inline-flex min-h-[44px] -translate-x-1/2 items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-medium text-white shadow-md transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
        >
          <ChevronDown className="h-3.5 w-3.5" /> New messages
        </button>
      )}
      <TranscriptModal
        isOpen={!!transcriptModal}
        onClose={() => setTranscriptModal(null)}
        transcript={transcriptModal?.transcript || []}
        direction={transcriptModal?.direction ?? null}
        recruiter={transcriptModal?.recruiter ?? null}
        contactName={contactName}
        contactPhone={contactPhone}
        mask={mask}
      />
      <FormResponseModal
        responseId={responseModal}
        onClose={() => setResponseModal(null)}
        mask={mask}
      />
      {deleting && (
        <NoteDeleteDialog
          open
          contactId={contactId}
          note={deleting}
          onDeleted={onDeleted}
          onClose={() => setDeleting(null)}
          nameOf={nameOf}
        />
      )}
      {historyId && (
        <NoteHistoryModal
          open
          onClose={() => setHistoryId(null)}
          contactId={contactId}
          noteId={historyId}
          nameOf={nameOf}
        />
      )}
    </div>
  );
}
